// src/services/api.ts

// Vite environment type declaration
/// <reference types="vite/client" />

interface Post {
  id: string;
  content: string;
  scheduled_time: string;
  status: 'draft' | 'scheduled' | 'posted' | 'failed';
  platform: string;
  post_id?: string;
  image_url?: string | null;
  image_filename?: string | null;
}

interface CreatePostPayload {
  content: string;
  scheduled_time: string;
  platform: string;
  status: Post['status']; // Required, will always be set to 'draft' by default
  image_url?: string | null;
  image_filename?: string | null;
}

interface UpdatePostPayload {
  content?: string;
  scheduled_time?: string;
  platform?: string;
  status?: Post['status'];
  image_url?: string | null;
  image_filename?: string | null;
}

interface ImageUploadResponse {
  filename: string;
  url: string;
  original_filename: string;
}

interface AIContentResponse {
  text: string;
}

interface UserProfile {
  id: string;
  username: string;
  email: string;
  email_verified: boolean;
  first_name?: string;
  last_name?: string;
  bio?: string;
  created_at: string;
  updated_at: string;
}

interface UpdateProfilePayload {
  username?: string;
  first_name?: string | null;
  last_name?: string | null;
  bio?: string | null;
}

interface ApiError {
  error: string;
  status?: number;
  details?: any;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Helper function to consistently build API URLs
 */
const getFullUrl = (endpoint: string): string => {
  // Remove any trailing slash from base URL
  const baseUrl = API_BASE_URL.endsWith('/') 
    ? API_BASE_URL.slice(0, -1) 
    : API_BASE_URL;
  
  // Ensure endpoint starts with a slash
  const formattedEndpoint = endpoint.startsWith('/') 
    ? endpoint 
    : `/${endpoint}`;
  
  return `${baseUrl}${formattedEndpoint}`;
};

// Custom interface extending RequestInit to include URL for retries
interface FetchOptions extends RequestInit {
  url?: string;
}

// Custom Error class for HTTP errors, including status
class HttpError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

// Variable to prevent multiple concurrent refresh attempts
let isRefreshing = false;
// Queue for requests that failed due to 401 and are waiting for refresh
let failedQueue: { resolve: (value?: any) => void, reject: (reason?: any) => void, config: FetchOptions }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      // Update token in the original request config before resolving
      if (prom.config && prom.config.headers && token) {
        (prom.config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
      // Retry the original fetch call, ensuring url exists
      if (prom.config && prom.config.url) {
        prom.resolve(fetch(prom.config.url, prom.config));
      } else {
        // Should not happen if url is always added, but reject defensively
        prom.reject(new Error("Missing URL in queued request config for retry.")); 
      }
    }
  });
  failedQueue = [];
};

// The main fetch function with retry logic
const fetchWithRetry = async (endpoint: string, options: FetchOptions = {}): Promise<Response> => {
  const url = getFullUrl(endpoint);
  const token = localStorage.getItem('token');

  // Add Authorization header if not already present
  if (!options.headers) {
    options.headers = {};
  }
  if (!(options.headers as Record<string, string>)['Authorization'] && token) {
    (options.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  // Add url to options for retrying
  options.url = url;

  try {
    const response = await fetch(url, options);

    if (!response.ok && response.status === 401) {
      // If token is already being refreshed, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, config: options });
        });
      }

      isRefreshing = true;

      try {
        console.log("Access token expired or invalid. Attempting refresh...");
        // Use direct fetch for refresh, no retry needed here
        const refreshResponse = await fetch(getFullUrl('auth/refresh'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }, // Refresh token is in HttpOnly cookie
        });

        if (refreshResponse.ok) {
          const { access_token: newAccessToken } = await refreshResponse.json();
          localStorage.setItem('token', newAccessToken);
          console.log("Token refresh successful.");
          
          // Update Authorization header for the retried request
          if (options.headers) {
             (options.headers as Record<string, string>)['Authorization'] = `Bearer ${newAccessToken}`;
          }
          
          // Process the queue with the new token
          processQueue(null, newAccessToken);

          // Retry the original request with the new token
          return fetch(url, options); 
        } else {
          // Refresh failed, log out
          console.error("Token refresh failed.");
          processQueue(new Error("Token refresh failed"), null);
          // Trigger logout via handleApiError by throwing a custom 401 HttpError
          throw new HttpError("Token refresh failed", 401);
        }
      } catch (refreshError) {
        console.error("Error during token refresh attempt:", refreshError);
        processQueue(refreshError, null);
        // Re-throw error to be handled, ensure it's an HttpError with status
        if (refreshError instanceof Error) {
           throw new HttpError(refreshError.message, 401);
        } else {
           throw new HttpError("Unknown error during token refresh", 401);
        }
      } finally {
        isRefreshing = false;
      }
    }
    
    // For non-401 errors or successful responses
    // Check if response is NOT ok AND NOT 304 before treating as error
    if (!response.ok && response.status !== 304) {
      // Let handleApiError process other errors - pass the response directly
      await handleApiError(response);
    }
    return response; // Return successful response or let handleApiError throw

  } catch (error: unknown) {
    // Check if it's our custom HttpError with status 401
    if (error instanceof HttpError && error.status === 401) {
      // handleApiError is designed to throw, so we await its promise (which never resolves)
      await handleApiError(error);
      // This line should technically be unreachable, but throw defensively
      throw error; 
    } else if (error instanceof Error) {
       // Handle other generic errors (network errors etc.)
       console.error("Fetch error:", error.message);
       throw error; // Rethrow other errors
    } else {
       // Handle non-Error throws if necessary
       console.error("Non-Error thrown during fetch:", error);
       throw error;
    }    
  }
};

const handleApiError = async (responseOrError: Response | HttpError | Error): Promise<never> => {
  let errorData: ApiError = { error: 'Unknown error', status: 500 };
  let status = 500;

  if (responseOrError instanceof Response) {
      status = responseOrError.status;
      try {
          const parsedError = await responseOrError.json();
          errorData = { ...parsedError, status };
      } catch {
          errorData = { error: `HTTP error ${status}`, status };
      }
  } else if (responseOrError instanceof HttpError) {
      // If it's our custom HttpError passed from fetchWithRetry
      errorData = { error: responseOrError.message, status: responseOrError.status || 500 };
      status = responseOrError.status || 500;
  } else {
      // Should not happen if called correctly, but handle defensively
      if (responseOrError instanceof Error) { // Handle generic Error if passed somehow
        errorData = { error: responseOrError.message, status: 500 };
      } else {
        errorData = { error: "An unexpected error occurred", status: 500 };
      }
  }


  // --- UPDATED 401 Handling ---
  if (status === 401) {
    console.error("API call failed with 401 Unauthorized. Refresh likely failed or token invalid.", errorData);
    
    // Prevent redirect loops if already on login page
    if (window.location.pathname !== '/login') {
      // Clear authentication tokens from storage
      localStorage.removeItem('token');
      // No need to remove refresh token cookie here, server handles invalidation
      
      // Dispatch custom event instead of direct redirect
      window.dispatchEvent(new CustomEvent('token-expired'));
      console.log("Dispatched 'token-expired' event.");
    }
  }
  // --- END UPDATED 401 Handling ---
  
  // Throw the error to be caught by the calling function (e.g., in react-query)
  throw new Error(errorData.error || `HTTP error ${status}`);
};

// Function to handle token refresh attempts - NO LONGER NEEDED directly
/* 
// ... commented out refreshToken function ...
*/

class AuthApi {
  static async login(username: string, password: string): Promise<{ access_token: string, user: any }> { // Update return type
    const url = getFullUrl('auth/login'); // Corrected endpoint
    
    try {
      // Use direct fetch for login, no retry needed
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });
      
      if (!response.ok) {
         // Use await here
        await handleApiError(response); 
      }

      const data = await response.json();
      
      // Backend login route now returns {'access_token': ..., 'user': ...}
      // The refresh token is set via HttpOnly cookie by the backend
      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
      } else {
         throw new Error("Login response missing access_token");
      }
      
      if (!data.user) {
         throw new Error("Login response missing user data");
      }
      
      return data; // Return the whole object { access_token, user }
    } catch (error) {
      console.error('Login API error:', error);
      // Rethrow the error after logging
      throw error; 
    }
  }

  // Validate token is no longer needed as refresh handles it implicitly
  /*
  static async validateToken(token: string): Promise<boolean> {
    // ... removed ...
  }
  */
  
   static async logout(): Promise<void> {
     // Pass only the endpoint to fetchWithRetry
     const endpoint = 'auth/logout'; 
     try {
       // fetchWithRetry will call getFullUrl internally
       await fetchWithRetry(endpoint, { 
         method: 'POST',
         headers: { 'Content-Type': 'application/json' } // Auth header added by fetchWithRetry
       });
     } catch (error) {
       // Log error but proceed with client-side cleanup in AuthProvider
       console.error('Logout API call failed:', error);
       // Don't rethrow here, let AuthProvider handle UI changes regardless
     }
   }
   
   // Add other Auth related API calls if needed (register, verify, reset password etc.)
   // These typically don't need the retry logic as they are unauthenticated 
   // or use specific tokens (verification/reset tokens)
}

class PostsApi {
  // Store the last ETag to use for subsequent requests
  private static lastETag: string | null = null;

  static async getPosts(): Promise<Response> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      // Auth header will be added by fetchWithRetry
    };
    
    if (PostsApi.lastETag) {
      headers['If-None-Match'] = PostsApi.lastETag;
    }
    
    // Use fetchWithRetry
    const response = await fetchWithRetry('posts', { headers });

    // Store the new ETag for future requests if present
    const newETag = response.headers.get('ETag');
    if (newETag) {
      PostsApi.lastETag = newETag;
    }

    // fetchWithRetry already handles non-ok statuses before returning here
    return response; // Return the whole response
  }

  static async createPost(post: CreatePostPayload): Promise<Post> {
    const response = await fetchWithRetry('posts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }, // Auth added by fetchWithRetry
      body: JSON.stringify(post),
    });
    return response.json();
  }

  static async updatePost(id: string, post: UpdatePostPayload): Promise<Post> {
     const response = await fetchWithRetry(`posts/${id}`, {
       method: 'PUT',
       headers: { 'Content-Type': 'application/json' }, // Auth added by fetchWithRetry
       body: JSON.stringify(post),
     });
     return response.json();
   }

  static async deletePost(id: string): Promise<void> {
    await fetchWithRetry(`posts/${id}`, {
      method: 'DELETE',
      // No body needed, Auth added by fetchWithRetry
    });
    // No return value on success
  }

  static async postNow(id: string): Promise<Post> {
    // This might need specific handling if it's different from a standard PUT update
    // Assuming it's a PUT request to update status to 'post_now'
     const response = await fetchWithRetry(`posts/${id}`, {
       method: 'PUT',
       headers: { 'Content-Type': 'application/json' }, // Auth added by fetchWithRetry
       body: JSON.stringify({ status: 'post_now' }),
     });
     return response.json();
  }

  static async uploadImage(file: File): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetchWithRetry('posts/upload', {
        method: 'POST',
        body: formData,
        // Let browser set Content-Type for FormData
      });

      const data = await response.json();
      if (!response.ok) {
        // Throw HttpError so handleApiError receives the correct type
        throw new HttpError(data.error || 'Image upload failed', response.status);
      }
      return data;
    } catch (error) {
      // Ensure error is of a type handleApiError expects
      if (error instanceof Response || error instanceof HttpError || error instanceof Error) {
         return handleApiError(error);
      } else {
         // Handle unexpected error types if necessary, or wrap in a standard Error
         console.error("Caught unexpected error type in uploadImage:", error);
         return handleApiError(new Error('An unexpected error occurred during image upload'));
      }
    }
  }

  // --- New method to fetch image blob --- 
  static async getImageBlob(filename: string): Promise<Blob> {
    const endpoint = `posts/uploads/${filename}`; // Construct endpoint relative to API base
    try {
      // Use fetchWithRetry to automatically handle auth token and refresh
      const response = await fetchWithRetry(endpoint, {
        method: 'GET',
      });
      
      // Check response status explicitly here as fetchWithRetry might only handle 401 for refresh
      if (!response.ok) {
        // Try to get error message from response if possible
        let errorMsg = `Failed to fetch image: Status ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.error || errorMsg; 
        } catch (jsonError) { /* Ignore if response isn't JSON */ }
        throw new HttpError(errorMsg, response.status);
      }
      
      // Return the image data as a Blob
      return await response.blob();

    } catch (error) {
      // Re-throw error to be handled by the calling code (e.g., react-query mutation)
      // Or handle specific errors if needed
      console.error(`Error fetching image blob for ${filename}:`, error);
      // Ensure the caught error is thrown so react-query knows it failed
      if (error instanceof Error) {
        throw error; // Re-throw HttpError or standard Error
      } else {
        throw new Error('An unknown error occurred while fetching the image blob');
      }
    }
  }
  // --- End new method --- 
}

class AIApi {
  static async generateContent(prompt: string): Promise<string> {
     const response = await fetchWithRetry('ai/generate', { // Assuming this is the correct endpoint
       method: 'POST',
       headers: { 'Content-Type': 'application/json' }, // Auth added by fetchWithRetry
       body: JSON.stringify({ input: prompt }), // Change 'prompt' key to 'input'
     });
     const data: AIContentResponse = await response.json();
     return data.text;
   }
}

// Add new SettingsApi for backend-synced settings
interface SystemSettings {
  // AI settings
  aiSystemPrompt?: string;
  temperature?: number;
  webSearchEnabled?: boolean;
  
  // Preferences settings
  optimalPostingTimes?: Array<{
    name: string;
    hour: number;
    minute: number;
  }>;
  
  // Post management settings
  autoArchiveDays?: number;
  defaultPlatform?: string;
}

class SettingsApi {
  static async getSystemSettings(): Promise<SystemSettings> {
    const response = await fetchWithRetry('settings'); // Assuming GET is default
    return response.json();
  }

  static async updateSystemSettings(settings: Partial<SystemSettings>): Promise<SystemSettings> {
     const response = await fetchWithRetry('settings', {
       method: 'PUT',
       headers: { 'Content-Type': 'application/json' }, // Auth added by fetchWithRetry
       body: JSON.stringify(settings),
     });
     return response.json();
   }

  // Assuming AI settings sync is just a partial update
  static async syncAISettings(settings: {
    aiSystemPrompt?: string;
    temperature?: number;
    webSearchEnabled?: boolean;
  }): Promise<SystemSettings> {
    return SettingsApi.updateSystemSettings(settings);
  }

  static async resetAISettings(): Promise<SystemSettings> {
     // Assuming a dedicated endpoint or a specific payload for reset
     // Example using a DELETE request, adjust if different
     const response = await fetchWithRetry('settings/ai', { 
       method: 'DELETE' 
     });
     return response.json();
   }
}

// Define interface for notification settings
interface NotificationSettings {
  post_notifications: {
    successful_posts: boolean;
    failed_posts: boolean;
    scheduled_posts: boolean;
  };
  account_notifications: {
    platform_connection_changes: boolean;
    security_events: boolean;
  };
  frequency: 'immediate' | 'daily' | 'weekly';
  email?: string;
}

// API class for managing notification settings
class NotificationSettingsApi {
  static async getSettings(): Promise<NotificationSettings> {
    const response = await fetchWithRetry('notification-settings'); // Assuming GET is default
    return response.json();
  }

  static async updateSettings(settings: NotificationSettings): Promise<NotificationSettings> {
     const response = await fetchWithRetry('notification-settings', {
       method: 'PUT',
       headers: { 'Content-Type': 'application/json' }, // Auth added by fetchWithRetry
       body: JSON.stringify(settings),
     });
     return response.json();
   }
}

class UserApi {
   // Deprecated? AuthProvider has checkAuthStatus, and /me endpoint exists
   /*
   static async getProfile(): Promise<UserProfile> {
     const response = await fetchWithRetry('profile'); // Assuming /profile endpoint
     return response.json();
   }
   */

  static async updateProfile(profile: UpdateProfilePayload): Promise<UserProfile> {
     const response = await fetchWithRetry('auth/profile', { // Assuming /auth/profile endpoint
       method: 'PUT',
       headers: { 'Content-Type': 'application/json' }, // Auth added by fetchWithRetry
       body: JSON.stringify(profile),
     });
     return response.json();
   }

  // Use this for fetching detailed user info *after* authentication
  static async getCurrentUser(): Promise<UserProfile> {
     const response = await fetchWithRetry('auth/me'); // Assuming /auth/me endpoint
     const data = await response.json();
     // The backend returns {'user': {...}}
     if (data && data.user) {
       return data.user;
     }
     throw new Error("Failed to get current user data structure");
   }
   
  static async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
     const response = await fetchWithRetry('auth/change-password', { // Assuming endpoint
       method: 'POST',
       headers: { 'Content-Type': 'application/json' }, // Auth added by fetchWithRetry
       body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
     });
     return response.json();
   }

  static async getSocialAccounts(): Promise<{ platform: string; account_username: string; is_connected: boolean }[]> {
     const response = await fetchWithRetry('auth/social-accounts'); // Assuming endpoint
     return response.json();
  }
  
  // Add other user-related API calls like deleteSocialAccount if needed
  static async deleteSocialAccount(platform: string): Promise<void> {
     await fetchWithRetry(`auth/social-accounts/${platform}`, { // Assuming endpoint
       method: 'DELETE',
     });
     // No return value on success
  }
}

// Export all classes and interfaces needed elsewhere
export {
  AuthApi, PostsApi, AIApi, SettingsApi, NotificationSettingsApi, UserApi,
};

// Export types/interfaces separately
export type {
  Post, CreatePostPayload, UpdatePostPayload, ImageUploadResponse, AIContentResponse, 
  UserProfile, UpdateProfilePayload, SystemSettings, NotificationSettings
};